import React, { useState } from 'react';
import { Linking, Modal } from "react-native"
import { Icons } from '../../Components';
import { WalletApi } from '../api';
import type { AccountProps, PlayerAddressProps } from '../../types';
import AddressManager from './AddressManager';
import AddressCard from './AddressCard';
import { useColors } from '../../constants/useColors';
import { Button, Text, View } from '../../Components/Themed';

type LinkAccountManagerProps = {
    ibt_placeholer_account:AccountProps,
    player_addresses:PlayerAddressProps[],
    onCancel: () => void
    onComplete: (account:AccountProps) => void
}

const LinkAccountManager = ({ player_addresses, ibt_placeholer_account, onCancel, onComplete }: LinkAccountManagerProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ try_counter, setTryCounter ] = useState(0);
    const [ show_addresses, setShowAddresses ] = useState<{
        visible:boolean,
        selected_address?:PlayerAddressProps
    }>({
        visible: false
    });
    const [ linking, setLinking ] = useState(false);
    const [ account, setAccount ] = useState<AccountProps>();
    const [ error, setError ] = useState<string>();
    const [ redirect_url, setRedirectUrl ] = useState<string>();

    
    const handleStartLink = async() => {
        if(loading || !show_addresses.selected_address){ return }//Prevent button mashing
        setLoading(true);
        Linking.addEventListener('url', handler)
        const a = await WalletApi.linkACHAccount(ibt_placeholer_account.account_id, show_addresses.selected_address.player_address_id)
        setAccount(a);
        if(a.status != 'redirect' || !a.redirect_url){ setError('Unable to process') }
        else { setRedirectUrl(a.redirect_url) }
        setLoading(false);
    }

    const handler = (ev:any) => {
        console.log(ev)
    }

    const tryGetAccount = async() => {
        if(!account){ return }
        if(try_counter > 60){ return } //Stop trying after 75 attempts
        const a = await WalletApi.getAccountById(account.account_id)
        setTryCounter(try_counter + 1)
        setAccount(a);
        if(a.status == 'redirect'){
            setTimeout(() => {
                console.log('trying again!')
                tryGetAccount()
            }, 4000);
        } else {
            setTryCounter(0);
            onComplete(a)
        }
    }

    const handleNavOut = async() => {
        if(!redirect_url){ return }
        setLinking(true);
        Linking.openURL(redirect_url)
        setTimeout(() => {
            tryGetAccount()
        }, 18000);
    }

    const valid = show_addresses.selected_address ? true : false

    return (
        <View style={{ flex:1 }}>
            <View type='header' style={{ padding:20 }}>
                <Text theme='h1'>LINK NEW ACCOUNT</Text>
                <Text style={{ marginTop:10 }} size={16} theme='description'>Link an account via instant bank transfer and get access to instant deposits and withdraws.</Text>
            </View>
            <View style={{ flex:1, justifyContent:'center', alignItems:'center' }}>
                <View float style={{ padding:10, backgroundColor:Colors.text.white }}>
                    <Icons.IBTFLowIcon size={350} />
                </View>
                <View style={{ padding:20, marginTop:20}}>
                    <Text size={16} theme='light' textAlign='center' style={{marginBottom:2}}>Using <Button style={{padding:0}} type='text' onPress={() => Linking.openURL('https://plaid.com/safety')}><Text weight='bold' color={Colors.text.action} size={18}>Plaid</Text></Button>, you will be asked to securly login to your bank and select the account you wish to link and transact from.</Text>
                    {show_addresses.selected_address ?
                    <View style={{ padding:20 }}>
                        <Text style={{ marginBottom:10 }} textAlign='center' theme='h1'>Billing Address</Text>
                        <AddressCard
                            address={show_addresses.selected_address}
                            hide_delete
                            onDelete={() => console.log('')}
                            onSelect={() => setShowAddresses({ ...show_addresses, visible:true })}
                        />  
                    </View>
                    :<></>}
                </View>
            </View>
            
            {error?
            <Text size={14} color={Colors.text.warning}>{error}</Text>
            :<></>}
            {show_addresses.selected_address && (!account || account.status == 'redirect') ?
            <View nativeID='link_actions' style={{ flexDirection:'row', padding:20  }}>
                <Button
                    title='CANCEL'
                    style={{ flex:1, marginRight:5 }}
                    type='error'
                    onPress={() => onCancel()}
                />
                {!redirect_url ?
                <Button
                    style={{ flex:3, opacity:loading || !valid?0.5:1 }}
                    disabled={loading || !valid}
                    title='START ACCOUNT LINK'
                    loading={loading}
                    type='success'
                    onPress={() => handleStartLink()}
                />
                :
                <Button
                    style={{ flex:3, opacity:linking?0.5:1 }}
                    disabled={linking}
                    title='FINISH WITH PLAID'
                    loading={linking}
                    type='success'
                    onPress={() => handleNavOut()}
                />
                }
            </View>
            :!show_addresses.selected_address ?
            <View style={{ marginTop:10, borderRadius:8, padding:20 }}>
                <Text size={14} theme='description'>Before we continue, we will need get the billing address associated with the account to be linked</Text>
                <View transparent style={{ flexDirection:'row', alignItems:'center', marginTop:10 }}>
                    <Button
                        title='CANCEL'
                        style={{ flex:1, marginRight:4 }}
                        type='error'
                        onPress={() => onCancel()}
                    />
                    <Button
                        style={{ flex:2, marginLeft:4 }}
                        title={show_addresses.selected_address ?'CHANGE':'ADD BILLING ADDRESS'}
                        type='success'
                        onPress={() => setShowAddresses({ ...show_addresses, visible:true })}
                    />
                </View>
            </View>
            :<></>}
            <Modal
                visible={show_addresses.visible}
                transparent
                animationType='slide'
                style={{flex:1}}
            >
                <View type='blur' style={{ flex:1 }}>
                    <AddressManager
                        player_addresses={player_addresses}
                        onSelect={(addy) => setShowAddresses({ visible:false, selected_address:addy })}
                        onAddAddress={(addy) => setShowAddresses({ visible:false, selected_address:addy })}
                        onCancel={() => setShowAddresses({ ...show_addresses, visible:false })}
                    />
                </View>
            </Modal>
            <Modal
                visible={account && account.status != 'redirect' ? true : false}
                transparent
                animationType='slide'
                style={{flex:1}}
            >
                {account && account.status != 'redirect' ?
                <View type='blur' style={{ flex:1 }}>
                    {account.status == 'active' ?
                    <View style={{ flex:1, padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                        <Icons.TransactionSuccessIcon  size={150} />
                        <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.text.success, marginTop:20 }}>
                            <Text size={20} color={Colors.text.success} weight='bold' textAlign='center'>Account Linked Successfully</Text>
                            <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Your account has successfully been added. Please select close below to continue.</Text>
                        </View>
                    </View>
                    : 
                    <View style={{ flex:1, padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                        <Icons.TransactionErrorIcon  size={150} />
                        <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.text.error, marginTop:20 }}>
                            <Text size={20} color={Colors.text.error} weight='bold' textAlign='center'>Failed to Link Account</Text>
                            <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Sorry, we were unable to link your account at this time.  Please try again or reach out to support for help.</Text>
                        </View>
                    </View>
                    }
                    <View nativeID='action_row' style={{ flexDirection:'row', padding:20 }}>
                        <Button
                            style={{ flex:1, opacity:1 }}
                            title='CLOSE'
                            type='error'
                            onPress={() => {
                                if(account.status == 'active'){ return onComplete(account) }
                                return onCancel()
                            }}
                        />
                    </View>
                </View>
                :<></>}
            </Modal>
        </View>
    )
}

export default LinkAccountManager